import { useContext, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, CircularProgress, List, Popover, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { AuthStoreContext } from '../../Root';
import { NodeService } from '../../../http/node-service';
import NodePatchStore from '../../../stores/node-patch-store';
import NodePickerItem from '../../admin/NodePickerItem';
import { DbNode } from '../../../types/node';

export interface NodeFilterProps {
    selectedNodeId?: number;
    onChange: (nodeId: number) => void;
}

const NodeFilter = observer(({ selectedNodeId, onChange }: NodeFilterProps) => {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);

    const [rootNode, setRootNode] = useState<DbNode | null>(null);
    const [selectedNode, setSelectedNode] = useState<DbNode | null>(null);
    const [expandedIds, setExpandedIds] = useState<Set<number> | null>(null);
    const [currentNodeId, setCurrentNodeId] = useState<number | null>(null);

    useEffect(() => {
        if (!user) return;

        const effectiveId = selectedNodeId ?? user.node_id;
        setCurrentNodeId(effectiveId);

        NodeService.getById(user.node_id)
            .then((r) => {
                const root = r.data as DbNode;
                setRootNode(root);
                if (effectiveId === user.node_id) setSelectedNode(root);
            })
            .catch(() => {});

        if (selectedNodeId != null && selectedNodeId !== user.node_id) {
            NodeService.getById(selectedNodeId)
                .then((r) => setSelectedNode(r.data as DbNode))
                .catch(() => {});

            const pathStore = new NodePatchStore(selectedNodeId);
            pathStore
                .load()
                .then(() => {
                    const ids = new Set(pathStore.nodes.map((n) => n.node_id));
                    ids.add(user.node_id);
                    setExpandedIds(ids);
                })
                .catch(() => setExpandedIds(new Set([user.node_id])));
        } else {
            setExpandedIds(new Set([user.node_id]));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    const handleSelect = (node: DbNode) => {
        setCurrentNodeId(node.node_id);
        setSelectedNode(node);
        onChange(node.node_id);
        setOpen(false);
    };

    const loading = rootNode === null || expandedIds === null;

    return (
        <Box sx={{ mx: 1, mb: 1 }}>
            <Box
                ref={anchorRef}
                onClick={() => !loading && setOpen(true)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 40,
                    px: 1.5,
                    border: '1px solid',
                    borderColor: open ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    cursor: loading ? 'default' : 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: loading ? 'divider' : 'text.primary' },
                }}
            >
                {loading ? (
                    <CircularProgress size={14} sx={{ mx: 'auto' }} />
                ) : (
                    <>
                        <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {selectedNode?.name ?? '—'}
                        </Typography>
                        <ArrowDropDownIcon
                            fontSize="small"
                            sx={{
                                color: 'text.secondary',
                                flexShrink: 0,
                                transform: open ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                            }}
                        />
                    </>
                )}
            </Box>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: anchorRef.current?.offsetWidth ?? 0,
                            maxHeight: 320,
                            overflow: 'auto',
                            mt: 0.5,
                        },
                    },
                }}
            >
                <List dense disablePadding>
                    <NodePickerItem
                        node={rootNode!}
                        depth={0}
                        selectedNodeId={currentNodeId}
                        expandedIds={expandedIds!}
                        onSelect={handleSelect}
                    />
                </List>
            </Popover>
        </Box>
    );
});

export default NodeFilter;
